"use client"

import Link from "next/link"
import { useCallback, useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@clerk/nextjs"
import { ArrowLeft } from "lucide-react"

import { BillingIntervalToggle } from "@/components/billing/billing-interval-toggle"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { formatUsdWholeFromCents } from "@/lib/billing/format-usd"
import {
  planBillingCheckoutSuccessPath,
  planBillingManageHref,
} from "@/lib/billing/plan-billing-routes"
import {
  requestNewSelfServeSubscriptionCheckout,
  requestSelfServePlanChangeCheckout,
} from "@/lib/billing/billing-checkout-client"
import { trackBillingPricingViewed } from "@/lib/analytics/billing-analytics"
import { useBillingSummary } from "@/hooks/use-billing-summary"
import {
  APP_BILLING_LIFECYCLE,
  billingLifecycleWantsNewSubscriptionCheckout,
} from "@/features/billing/app-billing-lifecycle"
import { planTierFeatureBullets } from "@/features/billing/plan-public-display"
import {
  isSelfServePaidPlan,
  listPlanDefinitionsOrdered,
  planAnnualEffectiveMonthlyCents,
  planAnnualSavingsVsTwelveMonthlyPaymentsCents,
  type BillingInterval,
  type PlanDefinition,
  type PlanId,
} from "@/features/billing/plans"

const SUPPORT_EMAIL = "hello@maatii.co"

export function MarketingPricingPageClient() {
  const { isSignedIn } = useAuth()
  const { data: billing, loading: billingLoading } = useBillingSummary()
  const [interval, setInterval] = useState<BillingInterval>("monthly")
  const [busyPlan, setBusyPlan] = useState<PlanId | null>(null)

  const startCheckout = useCallback(
    async (plan: "pro" | "business") => {
      setBusyPlan(plan)
      try {
        const lifecycle = billing?.lifecycle ?? APP_BILLING_LIFECYCLE.FREE
        const r = billingLifecycleWantsNewSubscriptionCheckout(lifecycle)
          ? await requestNewSelfServeSubscriptionCheckout({ plan, interval })
          : await requestSelfServePlanChangeCheckout({ plan, interval })
        if (r.ok && "url" in r && r.url) window.location.href = r.url
        else if (r.ok && "applied" in r && r.applied) {
          window.location.href = planBillingCheckoutSuccessPath()
        }
      } finally {
        setBusyPlan(null)
      }
    },
    [interval, billing?.lifecycle],
  )

  const commerce = Boolean(billing?.rollout?.commerce)
  const tiers = listPlanDefinitionsOrdered()

  useEffect(() => {
    trackBillingPricingViewed()
  }, [])

  return (
    <main className="app-shell min-w-0 pb-20 pt-[var(--spacing-landing-hero-pt)] sm:pb-24">
      <div className="mx-auto max-w-[var(--max-width-mid)] px-[var(--space-md)] sm:px-0">
        <Link
          href="/"
          className="type-backlink mb-10 inline-flex items-center gap-2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="icon-sm" />
          Home
        </Link>
        <header className="text-center">
          <p className="type-marketing-kicker text-muted-foreground">Plans</p>
          <h1 className="type-hero-title mt-2 text-balance text-foreground">
            Pricing
          </h1>
          {isSignedIn && !billingLoading ? (
            <p className="type-caption mx-auto mt-5 max-w-md text-muted-foreground">
              You&apos;re signed in—open{" "}
              <Link
                href={planBillingManageHref()}
                className="font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Plan &amp; billing
              </Link>{" "}
              for live storage usage and checkout. This page is a quick overview.
            </p>
          ) : null}
          <BillingIntervalToggle
            className="mx-auto mt-8 flex flex-col items-center"
            value={interval}
            onChange={setInterval}
          />
        </header>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {tiers.map((def) => (
            <PricingTierCard
              key={def.id}
              def={def}
              interval={interval}
              isSignedIn={Boolean(isSignedIn)}
              billingLoading={Boolean(isSignedIn && billingLoading)}
              commerceEnabled={commerce}
              busyPlan={busyPlan}
              onCheckout={(plan) => void startCheckout(plan)}
            />
          ))}
        </div>
      </div>
    </main>
  )
}

function PricingTierCard({
  def,
  interval,
  isSignedIn,
  billingLoading,
  commerceEnabled,
  busyPlan,
  onCheckout,
}: {
  def: PlanDefinition
  interval: BillingInterval
  isSignedIn: boolean
  billingLoading: boolean
  commerceEnabled: boolean
  busyPlan: PlanId | null
  onCheckout: (plan: "pro" | "business") => void
}) {
  const effAnnualMo = planAnnualEffectiveMonthlyCents(def)
  const saveAnnual = planAnnualSavingsVsTwelveMonthlyPaymentsCents(def)
  const listMo = formatUsdWholeFromCents(def.monthlyPriceCents)
  const prepay = formatUsdWholeFromCents(def.annualPriceCents)
  const effMoStr = formatUsdWholeFromCents(effAnnualMo)

  const priceBlock =
    def.id === "enterprise" ? (
      <p className="type-body-sm font-medium text-foreground">Custom</p>
    ) : def.monthlyPriceCents === 0 || !listMo ? (
      <p className="type-body-sm font-medium text-foreground">$0</p>
    ) : interval === "monthly" ? (
      <div>
        <p className="type-body-sm font-semibold text-foreground">
          {listMo}
          <span className="font-normal text-muted-foreground"> / mo</span>
        </p>
        <p className="type-caption mt-1 text-muted-foreground">Billed monthly</p>
      </div>
    ) : effMoStr && prepay ? (
      <div>
        <p className="type-body-sm font-semibold text-foreground">
          {effMoStr}
          <span className="font-normal text-muted-foreground"> / mo</span>
        </p>
        <p className="type-caption mt-1 text-muted-foreground">
          {prepay} yearly prepay
        </p>
        {saveAnnual != null && saveAnnual > 0 ? (
          <p className="type-caption mt-1 text-foreground">
            Save {formatUsdWholeFromCents(saveAnnual)} vs 12 × monthly
          </p>
        ) : null}
      </div>
    ) : (
      <p className="type-body-sm font-medium text-foreground">{listMo}</p>
    )

  const signupHref = `/sign-up?redirect_url=${encodeURIComponent(planBillingManageHref())}`

  let cta: ReactNode = null
  if (def.id === "free") {
    cta = (
      <Button className="w-full" size="sm" variant="secondary" asChild>
        <Link href={signupHref}>Create account</Link>
      </Button>
    )
  } else if (def.id === "enterprise") {
    cta = (
      <Button className="w-full" size="sm" variant="secondary" asChild>
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Enterprise plan")}`}
        >
          Contact sales
        </a>
      </Button>
    )
  } else if (isSelfServePaidPlan(def.id)) {
    const busy = busyPlan === def.id
    cta = isSignedIn ? (
      <Button
        className="w-full"
        size="sm"
        variant="default"
        disabled={busy || billingLoading || !commerceEnabled}
        onClick={() => {
          if (!isSelfServePaidPlan(def.id)) return
          onCheckout(def.id)
        }}
      >
        {busy
          ? "Redirecting…"
          : billingLoading
            ? "…"
            : !commerceEnabled
              ? "Checkout unavailable"
              : `Upgrade to ${def.displayName}`}
      </Button>
    ) : (
      <Button className="w-full" size="sm" variant="secondary" asChild>
        <Link href={signupHref}>{`Sign up · ${def.displayName}`}</Link>
      </Button>
    )
  }

  const bullets = planTierFeatureBullets(def)

  return (
    <Card
      id={def.id === "enterprise" ? "enterprise" : undefined}
      data-plan={def.id}
      className={cn(
        "flex h-full flex-col border-border bg-card p-5 shadow-sm sm:p-6",
        def.id === "pro" && "ring-2 ring-ring ring-offset-2 ring-offset-background",
      )}
    >
      <div className="flex flex-1 flex-col gap-4">
        <div>
          <p className="type-card-title text-foreground">{def.displayName}</p>
          <div className="mt-3">{priceBlock}</div>
        </div>
        <ul className="type-caption flex flex-col gap-2 border-t border-border pt-4 text-muted-foreground">
          {bullets.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
      <div className="mt-6">{cta}</div>
    </Card>
  )
}
